/**
 * SCR-025 — Portal Schedule /portal/schedule
 * Weekly/list view of class schedules.
 * Mobile-first: list view by default on mobile, grid on md+.
 * Week navigation: prev/next/today buttons.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { usePortalSchedule } from '@client/lib/queries/use-portal';
import { useChildSelector } from '@client/store/child-selector';
import { authClient } from '@client/lib/auth-client';
import type { ScheduleSlot } from '@client/lib/queries/use-portal';

export const Route = createFileRoute('/portal/schedule')({
  component: PortalSchedule,
});

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const WEEKDAY_LABEL: Record<string, string> = {
  mon: 'Thứ 2', tue: 'Thứ 3', wed: 'Thứ 4', thu: 'Thứ 5',
  fri: 'Thứ 6', sat: 'Thứ 7', sun: 'Chủ nhật',
};

// Palette for class color coding — uses fun palette tokens (portal §2.4).
// Each entry: light bg / border / text + dark variants.
const CLASS_COLORS = [
  'bg-fun-blue-100 border-fun-blue text-fun-blue-700 dark:bg-fun-blue-700/20 dark:border-fun-blue/50 dark:text-fun-blue',
  'bg-brand-green-100 border-brand-green-300 text-brand-green-700 dark:bg-brand-green-950/40 dark:border-brand-green-700/50 dark:text-brand-green-300',
  'bg-fun-violet-100 border-fun-violet text-fun-violet-700 dark:bg-fun-violet-700/20 dark:border-fun-violet/50 dark:text-fun-violet',
  'bg-brand-orange-100 border-brand-orange-300 text-brand-orange-700 dark:bg-brand-orange-950/40 dark:border-brand-orange-700/50 dark:text-brand-orange-300',
  'bg-fun-coral-100 border-fun-coral text-fun-coral-700 dark:bg-fun-coral-700/20 dark:border-fun-coral/50 dark:text-fun-coral',
];

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDateRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

function isTodayWeekday(monday: Date, weekday: string): boolean {
  const today = new Date();
  const todayMonday = getMonday(today);
  if (monday.toDateString() !== todayMonday.toDateString()) return false;
  const dayIndex = today.getDay(); // 0=sun
  const names = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return names[dayIndex] === weekday;
}

function PortalSchedule() {
  const { data: session } = authClient.useSession();
  const role = (session?.user as { role?: string })?.role ?? '';
  const { selectedChildId } = useChildSelector();
  const studentId = role === 'parent' ? selectedChildId : undefined;

  const [monday, setMonday] = useState(() => getMonday(new Date()));
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    return (localStorage.getItem('portal-schedule-view') as 'list' | 'grid') ?? 'list';
  });

  const weekStart = monday.toISOString().split('T')[0]!;
  const { data, isLoading, isError, refetch } = usePortalSchedule(studentId, weekStart);

  function changeView(v: 'list' | 'grid') {
    setViewMode(v);
    localStorage.setItem('portal-schedule-view', v);
  }

  function prevWeek() {
    const d = new Date(monday);
    d.setDate(d.getDate() - 7);
    setMonday(d);
  }
  function nextWeek() {
    const d = new Date(monday);
    d.setDate(d.getDate() + 7);
    setMonday(d);
  }
  function goToday() { setMonday(getMonday(new Date())); }

  const slots = data?.slots ?? [];

  // Build color map per classId
  const classIds = [...new Set(slots.map((s) => s.classId))];
  const colorMap = new Map(classIds.map((id, i) => [id, CLASS_COLORS[i % CLASS_COLORS.length]!]));

  // Group slots by weekday
  const grouped = WEEKDAYS.map((wd) => ({
    weekday: wd,
    label: WEEKDAY_LABEL[wd]!,
    isToday: isTodayWeekday(monday, wd),
    slots: slots
      .filter((s) => s.weekday === wd)
      .sort((a, b) => a.startTime.localeCompare(b.startTime)),
  })).filter((g) => g.slots.length > 0);

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl font-bold">Lịch học tuần</h1>
        <Tabs value={viewMode} onValueChange={(v) => changeView(v as 'list' | 'grid')}>
          <TabsList className="h-8">
            <TabsTrigger value="list" className="text-xs px-3">Danh sách</TabsTrigger>
            <TabsTrigger value="grid" className="text-xs px-3 hidden md:flex">Lưới tuần</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Week navigation */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={prevWeek} aria-label="Tuần trước">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium min-w-[140px] text-center">
          {formatDateRange(monday)}
        </span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={nextWeek} aria-label="Tuần sau">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={goToday}>
          Hôm nay
        </Button>
      </div>

      {isLoading && <ScheduleSkeleton />}

      {isError && (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertCircle className="h-7 w-7 text-destructive" />
          <p className="text-sm text-muted-foreground">Không tải được lịch học</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />Thử lại
          </Button>
        </div>
      )}

      {!isLoading && !isError && slots.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
          Chưa có lịch học — chưa ghi danh lớp nào hoặc tuần này không có buổi học
        </div>
      )}

      {!isLoading && !isError && slots.length > 0 && (
        <>
          {/* Color legend */}
          <div className="flex flex-wrap gap-2">
            {classIds.map((id) => {
              const slot = slots.find((s) => s.classId === id);
              return (
                <span
                  key={id}
                  className={`text-xs px-2 py-0.5 rounded-full border font-medium ${colorMap.get(id)}`}
                >
                  {slot?.className ?? id}
                </span>
              );
            })}
          </div>

          {/* List view — always shown on mobile; shown on desktop in list mode */}
          {(viewMode === 'list' || window.innerWidth < 768) && (
            <div className="space-y-4">
              {grouped.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Tuần này không có buổi học nào
                </p>
              ) : (
                grouped.map((g) => (
                  <div key={g.weekday}>
                    <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${g.isToday ? 'text-primary' : ''}`}>
                      {g.label}
                      {g.isToday && <Badge className="text-[10px] h-4 px-1.5">Hôm nay</Badge>}
                    </h3>
                    <div className="space-y-2">
                      {g.slots.map((slot, i) => (
                        <SlotCard key={i} slot={slot} color={colorMap.get(slot.classId) ?? ''} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Grid view — desktop only */}
          {viewMode === 'grid' && (
            <div className="hidden md:grid grid-cols-7 gap-1 text-xs">
              {WEEKDAYS.map((wd) => {
                const g = grouped.find((g) => g.weekday === wd);
                const isToday = isTodayWeekday(monday, wd);
                return (
                  <div key={wd} className="min-h-[100px]">
                    <div className={`text-center py-1 font-semibold mb-1 rounded ${isToday ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      {WEEKDAY_LABEL[wd]}
                    </div>
                    <div className="space-y-1">
                      {(g?.slots ?? []).map((slot, i) => (
                        <Popover key={i}>
                          <PopoverTrigger asChild>
                            <div
                              className={`border rounded p-1 cursor-pointer leading-tight ${colorMap.get(slot.classId) ?? ''} ${slot.mode === 'single' ? 'border-dashed' : ''}`}
                            >
                              <div className="font-medium truncate">{slot.startTime}–{slot.endTime}</div>
                              <div className="truncate opacity-80">{slot.className}</div>
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 text-sm space-y-1">
                            <p className="font-semibold">{slot.className}</p>
                            <p className="text-muted-foreground">{slot.courseName}</p>
                            <p>{slot.startTime}–{slot.endTime}</p>
                            <p>{slot.roomName}</p>
                            {slot.teacherName && <p>GV: {slot.teacherName}</p>}
                            {slot.mode === 'single' && <Badge variant="secondary" className="text-[10px]">Buổi bù</Badge>}
                          </PopoverContent>
                        </Popover>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SlotCard({ slot, color }: { slot: ScheduleSlot; color: string }) {
  return (
    <Card className={`border ${color} `}>
      <CardContent className="py-2.5 px-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{slot.className}</p>
          <p className="text-xs opacity-80 truncate">{slot.courseName}</p>
          {slot.teacherName && (
            <p className="text-xs opacity-70">GV: {slot.teacherName}</p>
          )}
        </div>
        <div className="text-xs text-right shrink-0 space-y-1">
          <p className="font-medium">{slot.startTime}–{slot.endTime}</p>
          <p className="opacity-70">{slot.roomName}</p>
          {slot.mode === 'single' && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1">Buổi bù</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ScheduleSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
      ))}
    </div>
  );
}
